import { useTranslations } from '@vocab/react';
import {
  Box,
  Button,
  ButtonLink,
  Dialog,
  IconNewWindow,
  Inline,
  RadioGroup,
  RadioItem,
  Stack,
  Text,
} from 'braid-design-system';
import { type FC, useEffect, useState } from 'react';

import { JobPlacementPostType } from 'src/App/AppPageBlock/graphql/hirer-types.generated';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import { updateJobPlacement } from 'src/graphql/hirer/updateJobPlacement';

import { useExperimentConfig } from '../ExperimentContext';

import translations from './.vocab';
import { DataSvg } from './DataSvg';
import { SuccessHiveSvg } from './SuccessHifiveSvg';

interface JobPlacementDialogProps {
  isOpen: boolean;
  close: () => void;
  closeNudge: () => void;
  hideJobPlacementInLocalStorage: () => void;
  jobTitle: string;
  jobLocations: string;
  jobId: number;
  userId: number | null;
  advertiserId: number;
  trackJobPlacementDataSubmissionEvent: (
    optionSelected: 'Yes' | 'No' | 'Not Yet',
  ) => void;
  trackJobPlacementDialogDismissedEvent: (
    dialogName:
      | 'Candidate Management - Placement - Have you Placed'
      | 'Candidate Management - Placement - Improve Job Ad'
      | 'Candidate Management - Placement - Thanks for Helping',
  ) => void;
  trackJobPlacementDialogDisplay: (
    dialogName:
      | 'Candidate Management - Placement - Have you Placed'
      | 'Candidate Management - Placement - Improve Job Ad'
      | 'Candidate Management - Placement - Thanks for Helping',
  ) => void;
  trackJobPlacementViewReportButtonPressedEvent: () => void;
}

export const JobPlacementDialog: FC<JobPlacementDialogProps> = ({
  isOpen,
  close,
  closeNudge,
  jobTitle,
  jobLocations,
  jobId,
  userId,
  advertiserId,
  trackJobPlacementDataSubmissionEvent,
  trackJobPlacementDialogDismissedEvent,
  trackJobPlacementDialogDisplay,
  trackJobPlacementViewReportButtonPressedEvent,
  hideJobPlacementInLocalStorage,
}) => {
  const [currentJobPlacement, setCurrentJobPlacement] = useState<
    'YES' | 'NO' | 'STILL_LOOKING' | 'UNDEFINED'
  >('UNDEFINED');
  const [currentStep, setCurrentStep] = useState<
    'INPUT_STEP' | 'REDIRECT_TO_ANALYTICS_PAGE_STEP' | 'THANK_YOU_STEP'
  >('INPUT_STEP');
  const [shouldShowErrorMessage, setShouldShowErrorMessage] =
    useState<boolean>(false);
  const { t } = useTranslations(translations);
  const { router } = useAppConfig();
  const { isExperimentEnabled } = useExperimentConfig();

  const getTrackingDialogName: Record<
    'INPUT_STEP' | 'REDIRECT_TO_ANALYTICS_PAGE_STEP' | 'THANK_YOU_STEP',
    | 'Candidate Management - Placement - Have you Placed'
    | 'Candidate Management - Placement - Improve Job Ad'
    | 'Candidate Management - Placement - Thanks for Helping'
  > = {
    INPUT_STEP: 'Candidate Management - Placement - Have you Placed',
    REDIRECT_TO_ANALYTICS_PAGE_STEP:
      'Candidate Management - Placement - Improve Job Ad',
    THANK_YOU_STEP: 'Candidate Management - Placement - Thanks for Helping',
  };

  const getTrackingSelectedOption = () => {
    if (currentJobPlacement === 'YES') {
      return 'Yes';
    }

    if (currentJobPlacement === 'NO') {
      return 'No';
    }

    return 'Not Yet';
  };

  const getDialogTitle = () => {
    if (currentStep === 'INPUT_STEP') {
      return t(
        'Have you placed a candidate for {jobTitle} in {jobLocations} through SEEK?',
        {
          jobTitle,
          jobLocations,
        },
      );
    }

    if (currentStep === 'REDIRECT_TO_ANALYTICS_PAGE_STEP') {
      return t('Let’s help you improve your job ad');
    }

    return t('Thanks for helping to improve SEEK');
  };

  const getDialogIllustration = () => {
    if (currentStep === 'REDIRECT_TO_ANALYTICS_PAGE_STEP') {
      return <DataSvg />;
    }

    if (currentStep === 'THANK_YOU_STEP') {
      return <SuccessHiveSvg />;
    }
  };

  useEffect(() => {
    trackJobPlacementDialogDisplay(getTrackingDialogName[currentStep]);
    setCurrentJobPlacement('UNDEFINED');
    setCurrentStep('INPUT_STEP');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <Dialog
      id="job-placement-dialog"
      title={getDialogTitle()}
      illustration={getDialogIllustration()}
      onClose={() => {
        if (userId === null) {
          return;
        }
        if (!isExperimentEnabled('growth-placement-passive-flow')) {
          updateJobPlacement({
            advertiserId,
            submissionDateUtc: new Date().toISOString(),
            jobId,
            postType: JobPlacementPostType.DIRECT,
            submitted: false,
            userId,
          });
          hideJobPlacementInLocalStorage();
        } else if (
          currentStep !== 'INPUT_STEP' &&
          isExperimentEnabled('growth-placement-passive-flow') &&
          (currentJobPlacement === 'NO' || currentJobPlacement === 'YES')
        ) {
          closeNudge();
        }
        close();
        trackJobPlacementDialogDismissedEvent(
          getTrackingDialogName[currentStep],
        );
        setCurrentJobPlacement('UNDEFINED');
        setCurrentStep('INPUT_STEP');
      }}
      open={isOpen}
      data={{ testid: 'job-placement-dialog' }}
    >
      {currentStep === 'INPUT_STEP' && (
        <>
          <RadioGroup
            id="job-placement-dialog-radio-group"
            value={currentJobPlacement}
            onChange={({ currentTarget: { value } }) => {
              setShouldShowErrorMessage(false);
              setCurrentJobPlacement(
                value as 'YES' | 'NO' | 'STILL_LOOKING' | 'UNDEFINED',
              );
            }}
            tone={shouldShowErrorMessage ? 'critical' : 'neutral'}
            aria-labelledby="fieldLabel"
            message={
              shouldShowErrorMessage && t('Please select an answer to continue')
            }
          >
            <RadioItem
              data={{ testid: 'job-placement-radio-input-yes' }}
              label={t('Yes')}
              value="YES"
            />
            <RadioItem
              data={{ testid: 'job-placement-radio-input-no' }}
              label={t('No')}
              value="NO"
            />
            <RadioItem label={t("I'm still looking")} value="STILL_LOOKING" />
          </RadioGroup>
          <Inline space="none">
            <Button
              tone="brandAccent"
              variant="solid"
              onClick={() => {
                if (userId === null) {
                  return;
                }

                if (currentJobPlacement === 'UNDEFINED') {
                  setShouldShowErrorMessage(true);
                  return;
                }

                if (currentJobPlacement === 'STILL_LOOKING') {
                  if (!isExperimentEnabled('growth-placement-passive-flow')) {
                    updateJobPlacement({
                      advertiserId,
                      submissionDateUtc: new Date().toISOString(),
                      jobId,
                      postType: JobPlacementPostType.DIRECT,
                      submitted: true,
                      userId,
                    });
                    hideJobPlacementInLocalStorage();
                  }
                  trackJobPlacementDataSubmissionEvent(
                    getTrackingSelectedOption(),
                  );
                  trackJobPlacementDialogDisplay(
                    'Candidate Management - Placement - Improve Job Ad',
                  );
                  setCurrentStep('REDIRECT_TO_ANALYTICS_PAGE_STEP');
                  return;
                }

                if (currentJobPlacement === 'NO') {
                  updateJobPlacement({
                    advertiserId,
                    submissionDateUtc: new Date().toISOString(),
                    jobId,
                    postType: JobPlacementPostType.DIRECT,
                    submitted: true,
                    userId,
                    isPlaced: false,
                  });
                  hideJobPlacementInLocalStorage();
                  trackJobPlacementDataSubmissionEvent(
                    getTrackingSelectedOption(),
                  );
                  trackJobPlacementDialogDisplay(
                    'Candidate Management - Placement - Improve Job Ad',
                  );
                  setCurrentStep('REDIRECT_TO_ANALYTICS_PAGE_STEP');
                  return;
                }

                updateJobPlacement({
                  advertiserId,
                  submissionDateUtc: new Date().toISOString(),
                  jobId,
                  postType: JobPlacementPostType.DIRECT,
                  submitted: true,
                  userId,
                  isPlaced: true,
                });
                hideJobPlacementInLocalStorage();
                trackJobPlacementDataSubmissionEvent(
                  getTrackingSelectedOption(),
                );
                trackJobPlacementDialogDisplay(
                  'Candidate Management - Placement - Thanks for Helping',
                );
                setCurrentStep('THANK_YOU_STEP');
              }}
            >
              {t('Continue')}
            </Button>
          </Inline>
        </>
      )}

      {currentStep === 'REDIRECT_TO_ANALYTICS_PAGE_STEP' && (
        <Stack space="none" align="center">
          <Text>
            {t('Your ad performance report provides personalised tips.')}
          </Text>
          <Box paddingTop="medium">
            <ButtonLink
              icon={<IconNewWindow />}
              target="_blank"
              tone="brandAccent"
              variant="solid"
              onClick={() => {
                trackJobPlacementViewReportButtonPressedEvent();
                close();
                closeNudge();
              }}
              href={router.adPerformance({
                jobId: Number(jobId),
                ref: 'candiman popup',
              })}
            >
              {t('View report')}
            </ButtonLink>
          </Box>
        </Stack>
      )}

      {currentStep === 'THANK_YOU_STEP' && (
        <Stack space="none" align="center">
          <Text data={{ testid: 'job-placement-feedback-thanks-message' }}>
            {t('Your feedback helps improve the reports we offer.')}
          </Text>
          <Box paddingTop="medium">
            <Button
              aria-label="Close dialog"
              tone="brandAccent"
              variant="solid"
              onClick={() => {
                close();
                closeNudge();
              }}
            >
              {t('Close')}
            </Button>
          </Box>
        </Stack>
      )}
    </Dialog>
  );
};
